import type { NeighborhoodDetail } from '../../../../mocks/neighborhoodDetails';

interface OverviewStatsProps {
  neighborhood: NeighborhoodDetail;
}

export default function OverviewStats({ neighborhood }: OverviewStatsProps) {
  const stats = [
    {
      label: 'Walk Score',
      value: neighborhood.walkScore,
      suffix: '/100',
      icon: 'ri-walk-line',
      desc: neighborhood.walkScore >= 80 ? 'Very Walkable' : neighborhood.walkScore >= 60 ? 'Somewhat Walkable' : 'Car-Dependent',
      source: 'Walk Score®',
    },
    {
      label: 'Transit Score',
      value: neighborhood.transitScore,
      suffix: '/100',
      icon: 'ri-bus-line',
      desc: neighborhood.transitScore >= 70 ? 'Excellent Transit' : neighborhood.transitScore >= 50 ? 'Good Transit' : 'Some Transit',
      source: 'Walk Score®',
    },
    {
      label: 'Total Reviews',
      value: neighborhood.totalReviews.toLocaleString(),
      suffix: '',
      icon: 'ri-chat-quote-line',
      desc: 'From verified visitors',
      source: 'User Reviews',
    },
    {
      label: 'Dining Options',
      value: neighborhood.diningCount,
      suffix: '+',
      icon: 'ri-restaurant-line',
      desc: 'Restaurants & cafes',
      source: 'Business Directory',
    },
  ];

  return (
    <section className="py-12 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 p-5 rounded-2xl bg-gray-50/80 border border-gray-100"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${neighborhood.accentColor}12` }}
              >
                <i className={`${stat.icon} text-xl`} style={{ color: neighborhood.accentColor }}></i>
              </div>
              <div>
                <p className="text-2xl font-black text-gray-900">
                  {stat.value}{stat.suffix}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{stat.desc}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Source: {stat.source}</p>
              </div>
            </div>
          ))}
        </div>

        {/* About Section */}
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              About {neighborhood.name}
            </h2>
            <p className="text-gray-600 leading-relaxed text-[15px]">
              {neighborhood.longDescription}
            </p>
          </div>

          <div className="space-y-4">
            {/* Parking */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-parking-box-line text-teal-600"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">Parking</h4>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{neighborhood.parkingInfo}</p>
            </div>

            {/* Transit */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-bus-line text-teal-600"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">Public Transit</h4>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{neighborhood.transitInfo}</p>
            </div>

            {/* Nearby */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-map-pin-line text-teal-600"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">Nearby Attractions</h4>
              </div>
              <div className="space-y-2">
                {neighborhood.nearbyAttractions.map((a) => (
                  <div key={a.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${a.icon} text-sm text-gray-400`}></i>
                      </div>
                      <span className="text-xs text-gray-700">{a.name}</span>
                    </div>
                    <span className="text-[11px] text-gray-400">{a.distance}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Data Sources Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs text-gray-400 leading-relaxed">
            <strong>Data Sources:</strong> Walk Score® and Transit Score® ratings provided by Walk Score. Business counts and reviews from verified directory listings. Distance measurements via Google Maps. Information updated regularly to ensure accuracy.
          </p>
        </div>
      </div>
    </section>
  );
}
